import React, { useEffect, useMemo, useState } from "react";
import Head from "next/head";
import Layout from "../../layout";
import ErrorPage404 from "../../404";
import PostBody from "../../post-body";
import BlogAuthorHero from "./blog-author-hero";
import BlogPostHero from "./blog-post-hero";
import BlogScrollNav from "./blog-scroll-nav";
import BlogStickyCta from "./blog-sticky-cta";
import BlogScrollTop from "./blog-scroll-top";
import RelatedPosts from "./related-posts";
import { mwImage } from "../../../lib/common";

function slugifyHeading(title: string) {
  return title
    .replace(/[^\w\s]/gi, "")
    .replace(/\s+/g, "-")
    .toLowerCase();
}

type Props = {
  post: any;
  allCatPosts?: any;
  /** Canonical path when it differs from `/blog/${post.slug}/` */
  uri?: string;
};

/** Single-post layout matching pages/blog/[slug].js (single-blog-v3). */
export default function BlogSinglePostView({ post, allCatPosts, uri }: Props) {
  const [headings, setHeadings] = useState<{ id: string; title: string }[]>([]);

  const postContent = useMemo(
    () =>
      post?.content?.replace(
        /https:\/\/(middleware\.io\/backend|wpadmin\.middleware\.io)\/wp-content/g,
        "https://middleware.io/wp-content"
      ),
    [post?.content]
  );

  const featuredImage = mwImage(post?.featuredImage?.node?.sourceUrl);
  const layoutUri =
    uri ||
    `/blog/${String(post?.slug || "")
      .replace(/^\/|\/$/g, "")}/`;

  useEffect(() => {
    const sourceElement = document.querySelector("#sticky-banner");
    const targetDiv = document.querySelector("#sticky");
    if (sourceElement && targetDiv) {
      targetDiv.appendChild(sourceElement);
    }
    return () => {
      const originalLocation = document.querySelector("#parent-banner");
      if (sourceElement && originalLocation) {
        originalLocation.appendChild(sourceElement);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const elements = document.querySelectorAll(".main-content .post-body h2");
    const nextHeadings: { id: string; title: string }[] = [];

    elements.forEach((item) => {
      if (item.tagName !== "H2") return;
      const title = item.textContent?.trim() || "";
      if (!title) return;
      const id = slugifyHeading(title);
      item.setAttribute("id", id);

      if (!item.querySelector(":scope > a.anchor-link")) {
        item.innerHTML = `
          <a aria-label="Link to section"
             class="anchor-link"
             href="#${id}">
             ${title}
          </a>
        `;
      }

      nextHeadings.push({ id, title });
    });

    setHeadings(nextHeadings);

    const Faqaccordion = document.querySelectorAll(".schema-faq-section");
    const faqHandler = (e: Event) => {
      (e.currentTarget as HTMLElement).classList.toggle("active");
    };
    Faqaccordion.forEach((child) => {
      child.addEventListener("click", faqHandler);
    });

    const setupAccordion = () => {
      const faq_accordion = document.getElementsByClassName("sc_fs_faq");
      if (faq_accordion.length > 0) {
        for (let i = 0; i < faq_accordion.length; i++) {
          if (i === 0) {
            faq_accordion[i].classList.add("active");
          }
          faq_accordion[i].addEventListener("click", (event) => {
            for (let j = 0; j < faq_accordion.length; j++) {
              faq_accordion[j].classList.remove("active");
            }
            (event.currentTarget as HTMLElement).classList.add("active");
          });
        }
      }
    };

    setupAccordion();
    const observer = new MutationObserver(() => {
      setupAccordion();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      Faqaccordion.forEach((child) => {
        child.removeEventListener("click", faqHandler);
      });
    };
  }, [postContent]);

  if (!post) {
    return <ErrorPage404 />;
  }

  return (
    <>
      {featuredImage ? (
        <Head>
          <link
            rel="preload"
            as="image"
            href={featuredImage}
            fetchPriority="high"
          />
        </Head>
      ) : null}
      <Layout
        ogImage={featuredImage}
        isHomePage={false}
        pageTitle={post?.title}
        uri={layoutUri}
        metaTitle={post?.seo?.title}
        metaDesc={post?.seo?.metaDesc}
        metaRobotsNoindex={post?.seo?.metaRobotsNoindex}
        metaRobotsNofollow={post?.seo?.metaRobotsNofollow}
      >
        <div className="middle-wrapper site-page">
          <section className="single-blog-v3">
            <div className="blog-post-shell">
              <BlogPostHero post={post} />
            </div>

            <div className="blog-article-layout">
              <BlogStickyCta />
              <div className="blog-article-column main-content">
                <PostBody content={postContent} />
                <div id="sticky" />
              </div>
              <BlogScrollNav headings={headings} />
              <BlogScrollTop />
              <BlogAuthorHero post={post} />
            </div>
          </section>

          <RelatedPosts
            categories={(post?.categories?.edges ?? []).map(
              (e: { node: { name: string } }) => e.node.name
            )}
            selectedBlog={post}
            data={allCatPosts}
          />
        </div>
      </Layout>
    </>
  );
}
